import { ShareWorkspacesSection } from "@/components/acl/ShareWorkspacesSection";
import {
  partitionShareWorkspaces,
  toShareWorkspaces,
} from "@/components/acl/share-workspaces";
import {
  GrantParams,
  useGrantsData,
  type ShareAccess,
} from "@/data/use-grants-data";
import { useGroupData } from "@/data/use-groups-data";
import { useKitData } from "@/data/use-kits-data";
import { useShareTargetsData } from "@/data/use-share-targets-data";
import { useUserLocalSettings } from "@/hooks/use-user-local-settings";
import type { Kit } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faLock,
  faShareNodes,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

type GrantDialogProps = {
  groupUuid: string;
  objectName: string;
  notice?: string;
} & GrantParams;

const shareAccessFor = (
  isKit: boolean,
  kitType: Kit["type"] | undefined,
): ShareAccess | null => {
  if (!isKit) {
    return "read";
  }
  if (kitType === "project") {
    return "write";
  }
  if (kitType === "collection") {
    return "read";
  }
  return null;
};

export const GrantDialog = ({
  groupUuid,
  objectName,
  notice,
  ...grantParams
}: GrantDialogProps) => {
  const [open, setOpen] = useState(false);
  const [workspaceSearch, setWorkspaceSearch] = useState("");
  const { recentGroupUuids, rememberRecentGroup } = useUserLocalSettings();
  const kitUuid = "kitUuid" in grantParams ? grantParams.kitUuid : null;
  const { kit } = useKitData(kitUuid ? groupUuid : null, kitUuid);
  const shareAccess = shareAccessFor("kitUuid" in grantParams, kit?.type);
  const { group } = useGroupData(groupUuid);
  const {
    teams,
    people,
    isLoading: isShareTargetsLoading,
  } = useShareTargetsData(group?.organizationUuid);
  const {
    grants,
    isLoading: isGrantsLoading,
    upsert,
    revoke,
    setPublished,
  } = useGrantsData(grantParams);
  const isLoading = isGrantsLoading || isShareTargetsLoading;

  const ownerGrant = grants.find((grant) => grant.access === "owner");
  const isPublished = ownerGrant?.isPublished ?? false;
  const sharedByGroupUuid = new Map<string, ShareAccess>();
  for (const grant of grants) {
    if (grant.access === "owner") continue;
    sharedByGroupUuid.set(grant.group.uuid, grant.access);
  }
  const grantedGroupUuids = [...sharedByGroupUuid.keys()];

  const { selected, recent, unselected } = partitionShareWorkspaces({
    workspaces: toShareWorkspaces({
      teams,
      people,
      currentGroupUuid: groupUuid,
    }),
    grantedGroupUuids,
    recentGroupUuids,
    searchQuery: workspaceSearch,
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setWorkspaceSearch("");
  };

  const handlePublish = async () => {
    await setPublished(!isPublished);
  };

  const handleToggle = async (rowUuid: string, checked: boolean) => {
    if (!shareAccess) return;
    rememberRecentGroup(rowUuid);
    if (checked) {
      await upsert({ groupUuid: rowUuid, access: shareAccess });
      return;
    }
    await revoke({ groupUuid: rowUuid });
  };

  const handleSelectAll = () => {
    if (!shareAccess) return;
    [...recent, ...unselected].forEach((workspace) => {
      void upsert({
        groupUuid: workspace.groupUuid,
        access: shareAccess,
      });
    });
  };

  const handleUnselectAll = () => {
    selected.forEach((workspace) => {
      rememberRecentGroup(workspace.groupUuid);
      void revoke({ groupUuid: workspace.groupUuid });
    });
  };

  return (
    <div className="flex flex-row items-center gap-2">
      <Tooltip>
        <TooltipContent>
          Unpublished: hidden from members. The author can see it. Owner-group
          admins and creators can also see unpublished experts, scripts,
          templates, and skills. <br /> Published: visible to members of this
          workspace and any workspace it is shared with.
        </TooltipContent>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            onClick={() => void handlePublish()}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={isPublished ? faGlobe : faLock} />
            {isPublished ? "Published" : "Unpublished"}
          </Button>
        </TooltipTrigger>
      </Tooltip>

      {shareAccess && (
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button variant="outline" disabled={isLoading}>
              <FontAwesomeIcon
                icon={sharedByGroupUuid.size > 0 ? faShareNodes : faLock}
              />
              {sharedByGroupUuid.size > 0 ? "Shared" : "Not Shared"}
              {sharedByGroupUuid.size > 0 && (
                <Badge className="text-xs">{sharedByGroupUuid.size}</Badge>
              )}
            </Button>
          </DialogTrigger>
          <DialogContent className="overflow-y-auto sm:max-w-[800px]">
            <DialogHeader>
              <DialogTitle>Share</DialogTitle>
              <DialogDescription>
                Choose the workspaces that can access {objectName}.
              </DialogDescription>
              {notice && (
                <p className="text-muted-foreground text-sm">{notice}</p>
              )}
            </DialogHeader>

            <ShareWorkspacesSection
              totalWorkspaces={teams.length + people.length}
              selected={selected}
              recent={recent}
              unselected={unselected}
              searchQuery={workspaceSearch}
              onSearchQueryChange={setWorkspaceSearch}
              isLoading={isLoading}
              onEnable={(targetGroupUuid) =>
                void handleToggle(targetGroupUuid, true)
              }
              onDisable={(targetGroupUuid) =>
                void handleToggle(targetGroupUuid, false)
              }
              onSelectAll={handleSelectAll}
              onUnselectAll={handleUnselectAll}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
